import { type LucideIcon, TrendingDown, TrendingUp } from 'lucide-react';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';
import { cn } from '@/lib/utils';

export interface KPICardProps {
  title: string;
  value: string;
  icon: LucideIcon;
  /** Variation en %, signe utilisé pour la couleur et la flèche. */
  changePct?: number;
  /** Texte de contexte sous la variation (ex : « vs mois préc. »). */
  changeLabel?: string;
  /** Données de la mini sparkline (valeurs y). */
  sparkline?: number[];
  /** Couleur d'accent (hex), défaut brand-primary. */
  accent?: string;
  className?: string;
}

/**
 * Carte KPI : icône, titre, valeur principale, variation en %, mini sparkline.
 * Brief — composant #3.
 */
export function KPICard({
  title,
  value,
  icon: Icon,
  changePct,
  changeLabel,
  sparkline,
  accent = '#1a1a2e',
  className,
}: KPICardProps) {
  const isPositive = (changePct ?? 0) >= 0;
  const trendColor = isPositive ? 'var(--status-done)' : 'var(--status-stuck)';
  const sparkData = (sparkline ?? []).map((y, i) => ({ i, y }));
  const gradientId = `kpi-grad-${title.replace(/\W+/g, '')}`;

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-lg border border-border bg-surface-secondary p-5 shadow-sm transition-shadow hover:shadow-md',
        className,
      )}
    >
      <div className="flex items-start justify-between">
        <span className="label-caps">{title}</span>
        <span
          className="flex size-9 items-center justify-center rounded-md"
          style={{ backgroundColor: `${accent}1A`, color: accent }}
        >
          <Icon className="size-5" />
        </span>
      </div>

      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <p title={value} className="truncate text-2xl font-bold tabular-nums text-content-primary">{value}</p>
          {changePct !== undefined && (
            <p className="mt-1 flex items-center gap-1 text-xs font-semibold" style={{ color: trendColor }}>
              {isPositive ? <TrendingUp className="size-3.5" /> : <TrendingDown className="size-3.5" />}
              {isPositive ? '+' : ''}
              {changePct.toFixed(1)}%
              {changeLabel && <span className="font-normal text-content-muted">{changeLabel}</span>}
            </p>
          )}
        </div>

        {sparkData.length > 1 && (
          <div className="h-10 w-24 shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={sparkData} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={accent} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={accent} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="y"
                  stroke={accent}
                  strokeWidth={2}
                  fill={`url(#${gradientId})`}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
